/* ==========================================================================
   Arsenal & Surface Maintenance Manager (with Ball Deletion & 3D Spheres)
   ========================================================================== */

import { appState } from './state.js';
import { PRO_SHOP_BALL_CATALOG } from './ballCatalog.js';
function generate3DBallSVG(colors, coverType) {
  const c0 = (colors && colors[0]) ? colors[0] : '#ff5722';
  const c1 = (colors && colors[1]) ? colors[1] : '#b71c1c';
  const c2 = (colors && colors[2]) ? colors[2] : '#4a148c';

  const isPearl = coverType && coverType.includes('Pearl');
  const isHybrid = coverType && coverType.includes('Hybrid');
  const opacity = isPearl ? '0.7' : (isHybrid ? '0.5' : '0.3');
  const safeId = c0.replace(/[^a-zA-Z0-9]/g, '') + Math.floor(Math.random() * 10000);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100%" height="100%">
    <defs>
      <radialGradient id="surfGrad_${safeId}" cx="35%" cy="35%" r="65%">
        <stop offset="0%" stop-color="${c0}" />
        <stop offset="50%" stop-color="${c1}" />
        <stop offset="100%" stop-color="${c2}" />
      </radialGradient>
      <radialGradient id="shineGrad_${safeId}" cx="30%" cy="25%" r="35%">
        <stop offset="0%" stop-color="#ffffff" stop-opacity="${opacity}" />
        <stop offset="60%" stop-color="#ffffff" stop-opacity="0.08" />
        <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
      </radialGradient>
      <radialGradient id="depthShadow_${safeId}" cx="50%" cy="50%" r="50%">
        <stop offset="68%" stop-color="#000000" stop-opacity="0" />
        <stop offset="96%" stop-color="#000000" stop-opacity="0.6" />
        <stop offset="100%" stop-color="#000000" stop-opacity="0.95" />
      </radialGradient>
    </defs>
    <circle cx="50" cy="50" r="48" fill="url(#surfGrad_${safeId})" />
    <path d="M 18 32 Q 52 8 82 42 Q 62 82 18 32 Z" fill="${c0}" opacity="0.45" />
    <path d="M 12 68 Q 48 42 78 78 Q 32 92 12 68 Z" fill="${c1}" opacity="0.35" />
    <circle cx="50" cy="50" r="48" fill="url(#depthShadow_${safeId})" />
    <circle cx="50" cy="50" r="48" fill="url(#shineGrad_${safeId})" />
    <circle cx="34" cy="38" r="2.4" fill="#fbbf24" stroke="#d97706" stroke-width="0.5" />
    <circle cx="43" cy="46" r="3.2" fill="#0f172a" stroke="#1e293b" stroke-width="0.8" />
    <circle cx="57" cy="46" r="3.2" fill="#0f172a" stroke="#1e293b" stroke-width="0.8" />
    <circle cx="50" cy="62" r="3.8" fill="#0f172a" stroke="#1e293b" stroke-width="0.8" />
  </svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function getBallPhotoUrl(ball) {
  if (!ball) return null;
  if (ball.imageUrl) return ball.imageUrl;

  const fullName = `${ball.brand || ''} ${ball.name || ''}`.toLowerCase().trim();
  const nameOnly = (ball.name || '').toLowerCase().trim();

  const catalogMatch = PRO_SHOP_BALL_CATALOG.find(b => b.name && (fullName.includes(b.name.toLowerCase()) || nameOnly.includes(b.name.toLowerCase())));
  if (catalogMatch && catalogMatch.imageUrl) {
    return catalogMatch.imageUrl;
  }

  const brandSlug = (ball.brand || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const nameSlug = (ball.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const fullSlug = `${brandSlug}-${nameSlug}`;

  return `https://www.bowwwl.com/sites/default/files/styles/ball_thumbnail/public/bowling-balls/${fullSlug}.png`;
}

export function getBallMaintenanceStats(ball) {
  const totalGames = Number.isFinite(ball.gamesBowled) ? ball.gamesBowled : 0;
  const logs = Array.isArray(ball.maintenanceLogs) ? ball.maintenanceLogs : [];

  // Find most recent detox in logs
  const detoxLog = logs.find(l => {
    const act = (l.action || '').toLowerCase();
    return act.includes('detox') || act.includes('de-oil') || act.includes('deoil');
  });

  let detoxBaseGames;
  if (detoxLog && Number.isFinite(detoxLog.gamesBowled)) {
    detoxBaseGames = detoxLog.gamesBowled;
  } else if (Number.isFinite(ball.lastDetoxGames)) {
    detoxBaseGames = ball.lastDetoxGames;
  } else {
    detoxBaseGames = 0;
  }

  // Find most recent resurface in logs
  const resurfaceLog = logs.find(l => {
    const act = (l.action || '').toLowerCase();
    return act.includes('resurface');
  });

  let resurfaceBaseGames;
  if (resurfaceLog && Number.isFinite(resurfaceLog.gamesBowled)) {
    resurfaceBaseGames = resurfaceLog.gamesBowled;
  } else if (Number.isFinite(ball.lastResurfaceGames)) {
    resurfaceBaseGames = ball.lastResurfaceGames;
  } else {
    resurfaceBaseGames = 0;
  }

  const gamesSinceDetox = Math.max(0, totalGames - detoxBaseGames);
  const gamesSinceResurface = Math.max(0, totalGames - resurfaceBaseGames);

  return {
    totalGames,
    gamesSinceDetox,
    gamesSinceResurface,
    lastDetoxDate: detoxLog?.date || ball.lastDeoil || 'None',
    lastResurfaceDate: resurfaceLog?.date || ball.lastResurfaceDate || 'None',
    logs
  };
}

function formatLogDate(dateStr) {
  if (!dateStr) return 'Unknown Date';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[monthIndex] || parts[1]} ${day}, ${year}`;
    }
  } catch (e) {
    // fallback
  }
  return dateStr;
}

function getActionBadgeInfo(action) {
  const act = (action || '').toLowerCase();
  if (act.includes('detox') && act.includes('resurface')) {
    return { className: 'badge-maint-detox', icon: 'sparkles' };
  }
  if (act.includes('detox') || act.includes('de-oil') || act.includes('deoil')) {
    return { className: 'badge-maint-detox', icon: 'droplet' };
  }
  if (act.includes('resurface')) {
    return { className: 'badge-maint-resurface', icon: 'refresh-cw' };
  }
  if (act.includes('touch-up') || act.includes('touchup') || act.includes('sanding')) {
    return { className: 'badge-maint-touchup', icon: 'disc' };
  }
  if (act.includes('drill') || act.includes('plug')) {
    return { className: 'badge-maint-drill', icon: 'crosshair' };
  }
  return { className: 'badge-maint-default', icon: 'wrench' };
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/[&<>'"]/g, tag => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  }[tag] || tag));
}

export function initArsenal() {
  const gridContainer = document.getElementById('arsenalGridContainer');
  const countEl = document.getElementById('arsenalCount');
  const filterChips = document.querySelectorAll('.filter-chip');
  const btnAddBall = document.getElementById('btnAddBall');
  const modalAddBall = document.getElementById('addBallModal');
  const formAddBall = document.getElementById('addBallForm');
  const btnCloseAddBall = document.getElementById('btnCloseAddBall');
  const btnCancelAddBall = document.getElementById('btnCancelAddBall');
  const catalogSelect = document.getElementById('catalogBallSelect');

  // Maintenance Modal
  const modalMaint = document.getElementById('maintModal');
  const btnCloseMaint = document.getElementById('btnCloseMaint');
  const btnCancelMaint = document.getElementById('btnCancelMaint');
  const btnSaveMaint = document.getElementById('btnSaveMaint');
  const maintBallNameEl = document.getElementById('maintBallName');
  const maintDateInput = document.getElementById('maintDateInput');
  const maintGamesInput = document.getElementById('maintGamesInput');
  const maintActionSelect = document.getElementById('maintActionSelect');
  const maintGritInput = document.getElementById('maintGritInput');
  const maintNotesInput = document.getElementById('maintNotesInput');

  // Maintenance Log Modal
  const modalMaintLog = document.getElementById('maintLogModal');
  const maintLogBody = document.getElementById('maintLogBody');
  const btnCloseMaintLog = document.getElementById('btnCloseMaintLog');
  const btnCloseMaintLogFooter = document.getElementById('btnCloseMaintLogFooter');
  const btnOpenAddMaintFromLog = document.getElementById('btnOpenAddMaintFromLog');

  let activeFilter = 'all';
  let targetMaintBallId = null;
  let activeLogBallId = null;
  let editingBallId = null;
  let selectedCatalogColors = null;

  const modalTitleEl = document.getElementById('addBallModalTitle');
  const btnSubmitBallEl = document.getElementById('btnSubmitAddBall');

  function openModalForAdd() {
    editingBallId = null;
    selectedCatalogColors = null;
    if (modalTitleEl) modalTitleEl.textContent = 'Add Ball to Arsenal';
    if (btnSubmitBallEl) btnSubmitBallEl.textContent = 'Add Ball to Bag';
    if (formAddBall) formAddBall.reset();
    const catalogInput = document.getElementById('catalogSearchInput');
    if (catalogInput) catalogInput.value = '';
    updateSearchStatus('hidden');
    if (modalAddBall) modalAddBall.classList.remove('hidden');
  }



  function openModalForEdit(ballId) {
    const s = appState.get();
    const ball = s.arsenal.find(b => b.id === ballId);
    if (!ball) return;

    editingBallId = ballId;
    selectedCatalogColors = ball.colors || null;
    updateSearchStatus('hidden');

    if (modalTitleEl) modalTitleEl.textContent = `Edit Ball: ${ball.brand} ${ball.name}`;
    if (btnSubmitBallEl) btnSubmitBallEl.textContent = 'Save Ball Specs';

    const inputName = document.getElementById('inputBallName');
    const inputBrand = document.getElementById('inputBallBrand');
    const inputCover = document.getElementById('inputCoverType');
    const inputFinish = document.getElementById('inputFinish');
    const inputWeight = document.getElementById('inputWeight');
    const inputRG = document.getElementById('inputRG');
    const inputDiff = document.getElementById('inputDiff');

    if (inputName) inputName.value = ball.name || '';
    if (inputBrand) inputBrand.value = ball.brand || '';
    if (inputCover) inputCover.value = ball.coverType || 'Solid Reactive';
    if (inputFinish) inputFinish.value = ball.finish || '';
    if (inputWeight) inputWeight.value = ball.weight || 15;
    if (inputRG) inputRG.value = ball.rg || 2.48;
    if (inputDiff) inputDiff.value = ball.diff || 0.050;

    const catalogInput = document.getElementById('catalogSearchInput');
    if (catalogInput) catalogInput.value = `${ball.brand} ${ball.name}`;

    if (modalAddBall) modalAddBall.classList.remove('hidden');
  }

  // Status indicator helper for Bowwwl.com live search
  function updateSearchStatus(statusType, message, progress = 0) {
    const statusContainer = document.getElementById('bowwwlSearchStatus');
    if (!statusContainer) return;

    if (statusType === 'hidden' || !message) {
      statusContainer.classList.add('hidden');
      statusContainer.innerHTML = '';
      return;
    }

    statusContainer.classList.remove('hidden', 'status-searching', 'status-success', 'status-error');

    let iconName = 'search';
    let iconClass = 'status-icon';

    if (statusType === 'searching') {
      statusContainer.classList.add('status-searching');
      iconName = 'loader';
      iconClass += ' animate-spin';
    } else if (statusType === 'success') {
      statusContainer.classList.add('status-success');
      iconName = 'check-circle-2';
    } else if (statusType === 'error') {
      statusContainer.classList.add('status-error');
      iconName = 'alert-circle';
    }

    const showProgressBar = statusType === 'searching' || (progress > 0 && progress < 100);

    statusContainer.innerHTML = `
      <div class="status-header">
        <i data-lucide="${iconName}" class="${iconClass}"></i>
        <span class="status-text">${message}</span>
      </div>
      ${showProgressBar ? `
        <div class="bowwwl-progress-track">
          <div class="bowwwl-progress-fill" style="width: ${Math.min(100, Math.max(0, progress))}%;"></div>
        </div>
      ` : ''}
    `;

    if (window.lucide) window.lucide.createIcons();
  }

  // Live Pro Shop & Bowwwl.com Text Search
  function initCatalogDropdownAndSearch() {
    const catalogSearchInput = document.getElementById('catalogSearchInput');
    const catalogSearchResults = document.getElementById('catalogSearchResults');
    const btnLiveSearchBowwwl = document.getElementById('btnLiveSearchBowwwl');

    if (catalogSearchInput && catalogSearchResults) {
      catalogSearchInput.addEventListener('input', (e) => {
        const query = e.target.value.toLowerCase().trim();
        if (!query) {
          catalogSearchResults.classList.add('hidden');
          catalogSearchResults.innerHTML = '';
          updateSearchStatus('hidden');
          return;
        }

        const cleanQuery = query.replace(/[^a-z0-9\s]/g, ' ').trim();
        const queryWords = cleanQuery.split(/\s+/).filter(Boolean);

        const matches = PRO_SHOP_BALL_CATALOG.filter(b => {
          const fullStr = `${b.brand} ${b.name} ${b.coverType} ${b.coverName || ''} ${b.coreName || ''} ${b.finish || ''}`
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ');
          return queryWords.every(word => fullStr.includes(word));
        });

        catalogSearchResults.innerHTML = '';

        if (matches.length > 0) {
          matches.slice(0, 6).forEach(ball => {
            const item = document.createElement('div');
            item.className = 'search-result-item';

            const c0 = ball.colors && ball.colors[0] ? ball.colors[0] : '#3b82f6';
            const c1 = ball.colors && ball.colors[1] ? ball.colors[1] : '#1e3a8a';
            const c2 = ball.colors && ball.colors[2] ? ball.colors[2] : '#000000';

            item.innerHTML = `
              <div class="ball-sphere-sm" style="background: radial-gradient(circle at 8px 8px, ${c0} 0%, ${c1} 60%, ${c2} 100%);"></div>
              <div>
                <div class="search-result-title">${ball.brand} ${ball.name}</div>
                <div class="search-result-specs">${ball.coverType} • RG: ${ball.rg} • Diff: ${ball.diff} • ${ball.finish}</div>
              </div>
            `;

            item.addEventListener('click', () => {
              selectCatalogBall(ball);
              catalogSearchInput.value = `${ball.brand} ${ball.name}`;
              catalogSearchResults.classList.add('hidden');
              updateSearchStatus('success', `Match found (Catalog): <strong>${ball.brand} ${ball.name}</strong>`);
            });

            catalogSearchResults.appendChild(item);
          });
        }

        // Always add option to search Bowwwl online database
        const onlineItem = document.createElement('div');
        onlineItem.className = 'search-result-item';
        onlineItem.innerHTML = `
          <div>
            <div class="search-result-title" style="color: var(--accent-amber);"><i data-lucide="globe"></i> Search Bowwwl.com Database Live</div>
            <div class="search-result-specs">Query live specs for "${e.target.value.trim()}"</div>
          </div>
        `;
        onlineItem.addEventListener('click', async () => {
          catalogSearchResults.classList.add('hidden');
          const fetched = await fetchOnlineSpecs(catalogSearchInput.value.trim());
          if (fetched) {
            catalogSearchInput.value = `${fetched.brand} ${fetched.name}`;
          }
        });
        catalogSearchResults.appendChild(onlineItem);
        if (window.lucide) window.lucide.createIcons();

        catalogSearchResults.classList.remove('hidden');
      });

      // Pressing Enter in search input triggers live Bowwwl query
      catalogSearchInput.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const query = catalogSearchInput.value.trim();
          if (query) {
            catalogSearchResults.classList.add('hidden');
            const fetched = await fetchOnlineSpecs(query);
            if (fetched) {
              catalogSearchInput.value = `${fetched.brand} ${fetched.name}`;
            }
          }
        }
      });

      // Click icon button triggers live search
      if (btnLiveSearchBowwwl) {
        btnLiveSearchBowwwl.addEventListener('click', async () => {
          const query = catalogSearchInput.value.trim();
          if (query) {
            catalogSearchResults.classList.add('hidden');
            const fetched = await fetchOnlineSpecs(query);
            if (fetched) {
              catalogSearchInput.value = `${fetched.brand} ${fetched.name}`;
            }
          }
        });
      }

      // Hide results when clicking outside
      document.addEventListener('click', (evt) => {
        if (!catalogSearchInput.contains(evt.target) && !catalogSearchResults.contains(evt.target) && (!btnLiveSearchBowwwl || !btnLiveSearchBowwwl.contains(evt.target))) {
          catalogSearchResults.classList.add('hidden');
        }
      });
    }
  }

  // Fetch live ball specs from Bowwwl.com with live progress and status indicator reporting
  async function fetchOnlineSpecs(queryStr) {
    if (!queryStr) {
      updateSearchStatus('hidden');
      return null;
    }

    updateSearchStatus('searching', `Searching Bowwwl.com database for "${queryStr}"...`, 20);

    const lowerQuery = queryStr.toLowerCase().trim();
    const knownBrands = [
      "roto grip", "900 global", "columbia 300", "kr strikeforce",
      "storm", "hammer", "brunswick", "ebonite", "track", "dexter",
      "master", "motiv", "radical", "dv8", "piranha", "seismic"
    ];

    let detectedBrand = "";
    let ballName = queryStr;

    for (const b of knownBrands) {
      if (lowerQuery.startsWith(b)) {
        detectedBrand = b;
        ballName = queryStr.substring(b.length).trim();
        break;
      }
    }

    const formatTitle = (str) => str.split(/[\s-]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

    // 1. Primary Method: Call Cloudflare serverless function /api/pull-specs
    try {
      updateSearchStatus('searching', `Querying Bowwwl.com API endpoint...`, 40);
      let apiUrl = `/api/pull-specs?query=${encodeURIComponent(queryStr)}`;
      if (detectedBrand && ballName) {
        apiUrl = `/api/pull-specs?brand=${encodeURIComponent(detectedBrand)}&name=${encodeURIComponent(ballName)}`;
      }

      const apiRes = await fetch(apiUrl);
      if (apiRes.ok) {
        const data = await apiRes.json();
        if (data && data.name && !data.error) {
          updateSearchStatus('searching', `Extracting specs for ${data.brand} ${data.name}...`, 85);
          const ballData = {
            name: formatTitle(data.name),
            brand: formatTitle(data.brand),
            coverType: data.coverType || "Solid Reactive",
            coverName: data.coverName || data.coverType || "Solid",
            coreName: data.coreName || (data.coreType ? data.coreType + " Core" : "Symmetric Core"),
            finish: data.finish || "1500 Polish",
            rg: data.rg ? parseFloat(data.rg) : 2.48,
            diff: data.diff ? parseFloat(data.diff) : 0.050,
            imageUrl: data.imageUrl || null,
            colors: ['#00f2fe', '#4facfe', '#111111']
          };
          selectCatalogBall(ballData);
          updateSearchStatus('success', `Match found: <strong>${ballData.brand} ${ballData.name}</strong> (Specs auto-filled)`, 100);
          return ballData;
        }
      }
    } catch (err) {
      console.warn("Primary /api/pull-specs endpoint error/offline, trying secondary proxy fallback:", err);
    }

    // 2. Secondary Client-Side Fallback Proxy Loop
    updateSearchStatus('searching', `Primary API offline. Checking database proxies...`, 55);
    const slugify = (str) => str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    let brandCandidates = detectedBrand ? [detectedBrand] : [
      "storm", "roto grip", "900 global", "hammer", "brunswick", "motiv", "columbia 300", "ebonite", "track", "radical", "dv8"
    ];

    let html = null;
    let finalBrand = detectedBrand || "storm";
    let finalName = ballName;

    for (let i = 0; i < brandCandidates.length; i++) {
      const bCandidate = brandCandidates[i];
      const brandSlug = slugify(bCandidate);
      const nameSlug = slugify(ballName);
      const path = `/bowling-ball-database/${brandSlug}/${nameSlug}`;

      const stepProgress = 55 + Math.floor(((i + 1) / brandCandidates.length) * 35);
      updateSearchStatus('searching', `Searching database proxy (${bCandidate})...`, stepProgress);

      const candidateUrls = [
        `/bowwwl-proxy${path}`,
        `https://corsproxy.io/?https://www.bowwwl.com${path}`,
        `https://api.allorigins.win/raw?url=${encodeURIComponent('https://www.bowwwl.com' + path)}`
      ];

      for (const url of candidateUrls) {
        try {
          const res = await fetch(url);
          if (res.ok) {
            const txt = await res.text();
            if (txt && (txt.includes("field--name-field-coverstock-type") || txt.includes("field--name-field-factory-finish") || txt.includes("bowwwl.com"))) {
              html = txt;
              finalBrand = bCandidate;
              break;
            }
          }
        } catch (e) {
          console.warn(`Fetch error for ${url}:`, e);
        }
      }
      if (html) break;
    }

    let coverType = "Solid Reactive";
    let coverName = "";
    let coreType = "Symmetric";
    let coreName = "";
    let finish = "1500 Polish";
    let rg = 2.48;
    let diff = 0.050;

    if (html) {
      updateSearchStatus('searching', `Extracting specifications from database...`, 95);

      const coverTypeMatch = html.match(/field--name-field-coverstock-type[\s\S]*?field__item">([\s\S]*?)<\/div>/);
      if (coverTypeMatch) coverType = coverTypeMatch[1].trim();

      const coverNameMatch = html.match(/card-title"><a href="[^"]*coverstocks\/[^"]*">([\s\S]*?)<\/a>/);
      if (coverNameMatch) coverName = coverNameMatch[1].replace(/\s+Coverstock\s*$/i, "").trim();

      const coreTypeMatch = html.match(/field--name-field-core-type[\s\S]*?field__item">([\s\S]*?)<\/div>/);
      if (coreTypeMatch) coreType = coreTypeMatch[1].trim();

      const coreNameMatch = html.match(/card-header"><a href="[^"]*cores\/[^"]*"[^>]*>([\s\S]*?)<\/a>/);
      if (coreNameMatch) coreName = coreNameMatch[1].replace(/\s+Core\s*$/i, "").trim();

      const finishMatch = html.match(/field--name-field-factory-finish[\s\S]*?field__item">([\s\S]*?)<\/div>/);
      if (finishMatch) finish = finishMatch[1].trim();

      const lbs15Index = html.indexOf("15 pounds");
      if (lbs15Index !== -1) {
        const block = html.substring(lbs15Index, lbs15Index + 800);
        const rgMatch = block.match(/field--name-field-rg[\s\S]*?field__item">([\d.]+)</);
        const diffMatch = block.match(/field--name-field-differential[\s\S]*?field__item">([\d.]+)</);
        if (rgMatch) rg = parseFloat(rgMatch[1]);
        if (diffMatch) diff = parseFloat(diffMatch[1]);
      }

      if (!rg || !diff) {
        const rgMatch = html.match(/field--name-field-rg[\s\S]*?field__item">([\d.]+)</);
        const diffMatch = html.match(/field--name-field-differential[\s\S]*?field__item">([\d.]+)</);
        if (rgMatch && !rg) rg = parseFloat(rgMatch[1]);
        if (diffMatch && !diff) diff = parseFloat(diffMatch[1]);
      }

      const ballData = {
        name: formatTitle(finalName),
        brand: formatTitle(finalBrand),
        coverType,
        coverName: coverName || coverType,
        coreName: coreName || (coreType + " Core"),
        finish,
        rg: rg || 2.48,
        diff: diff || 0.050,
        colors: ['#00f2fe', '#4facfe', '#111111']
      };

      selectCatalogBall(ballData);
      updateSearchStatus('success', `Match found: <strong>${ballData.brand} ${ballData.name}</strong> (Specs auto-filled)`, 100);
      return ballData;
    }

    // No match found on Bowwwl database
    updateSearchStatus('error', `No match found on Bowwwl.com for "${queryStr}". Enter specs manually below.`, 100);
    return null;
  }

  function selectCatalogBall(selected) {
    if (!selected) return;

    selectedCatalogColors = selected.colors || null;

    const inputName = document.getElementById('inputBallName');
    const inputBrand = document.getElementById('inputBallBrand');
    const inputCover = document.getElementById('inputCoverType');
    const inputFinish = document.getElementById('inputFinish');
    const inputRG = document.getElementById('inputRG');
    const inputDiff = document.getElementById('inputDiff');

    if (inputName) inputName.value = selected.name;
    if (inputBrand) inputBrand.value = selected.brand;
    if (inputCover) inputCover.value = selected.coverType;
    if (inputFinish) inputFinish.value = selected.finish;
    if (inputRG) inputRG.value = selected.rg;
    if (inputDiff) inputDiff.value = selected.diff;
  }

  function openModalForMaint(ballId, options = {}) {
    const s = appState.get();
    const ball = s.arsenal.find(b => b.id === ballId);
    if (!ball) return;

    targetMaintBallId = ballId;
    if (maintBallNameEl) {
      maintBallNameEl.textContent = `Logging maintenance for: ${ball.brand} ${ball.name}`;
    }
    if (maintDateInput) {
      maintDateInput.value = new Date().toISOString().slice(0, 10);
    }
    if (maintGamesInput) {
      maintGamesInput.value = ball.gamesBowled || 0;
    }
    if (maintGritInput) {
      maintGritInput.value = ball.finish || '';
    }
    if (maintNotesInput) {
      maintNotesInput.value = '';
    }
    if (maintActionSelect && options.action) {
      maintActionSelect.value = options.action;
    }
    if (modalMaint) {
      modalMaint.style.zIndex = '550';
      modalMaint.classList.remove('hidden');
    }
  }

  function openModalForMaintenanceLog(ballId) {
    const s = appState.get();
    const ball = s.arsenal.find(b => b.id === ballId);
    if (!ball || !modalMaintLog || !maintLogBody) return;

    activeLogBallId = ballId;

    const maintStats = getBallMaintenanceStats(ball);
    const photoUrl = getBallPhotoUrl(ball);
    const fallbackSvg = generate3DBallSVG(ball.colors, ball.coverType);
    const c1 = (ball.colors && ball.colors[0]) ? ball.colors[0] : '#ff5722';
    const c2 = (ball.colors && ball.colors[1]) ? ball.colors[1] : '#b71c1c';
    const c3 = (ball.colors && ball.colors[2]) ? ball.colors[2] : '#4a148c';

    const logs = maintStats.logs || [];

    let historyHtml = '';
    if (logs.length === 0) {
      historyHtml = `
        <div class="empty-maint-state">
          <i data-lucide="clipboard-list" class="empty-icon"></i>
          <h4>No Maintenance Logged Yet</h4>
          <p>Track detox baths, resurfacing, and surface touch-ups to keep your equipment reacting consistently at its peak.</p>
          <button type="button" class="btn-primary" id="btnEmptyLogAdd" style="margin-top: 14px; font-size: 0.8rem; padding: 8px 16px;">
            <i data-lucide="wrench"></i> Log First Maintenance
          </button>
        </div>
      `;
    } else {
      historyHtml = logs.map(entry => {
        const badge = getActionBadgeInfo(entry.action);
        const formattedDate = formatLogDate(entry.date);
        const gamesAgo = Math.max(0, (ball.gamesBowled || 0) - (entry.gamesBowled || 0));
        return `
          <div class="maint-log-item">
            <div class="maint-log-item-header">
              <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                <span class="maint-action-badge ${badge.className}">
                  <i data-lucide="${badge.icon}"></i> ${entry.action || 'Maintenance'}
                </span>
                <span class="maint-log-date">${formattedDate}</span>
              </div>
              <button class="btn-delete-log" data-log-id="${entry.id}" title="Delete Record">
                <i data-lucide="trash-2"></i>
              </button>
            </div>
            <div class="maint-log-item-specs">
              <span class="spec-tag">
                <i data-lucide="play-circle"></i> Bowled: <strong>${entry.gamesBowled || 0} gms</strong> (${gamesAgo} ago)
              </span>
              ${entry.grit ? `
                <span class="spec-tag">
                  <i data-lucide="disc"></i> Surface: <strong>${entry.grit}</strong>
                </span>
              ` : ''}
            </div>
            ${entry.notes ? `
              <div class="maint-log-item-notes">“${escapeHtml(entry.notes)}”</div>
            ` : ''}
          </div>
        `;
      }).join('');
    }

    maintLogBody.innerHTML = `
      <div class="maint-log-ball-header">
        <div class="ball-avatar-circle" title="Tap to twirl!" style="width: 44px; height: 44px; min-width: 44px; background: radial-gradient(circle at 14px 14px, ${c1} 0%, ${c2} 60%, ${c3} 100%);">
          <img src="${photoUrl}" alt="${ball.name}" class="ball-img-photo" onerror="this.onerror=null; this.src='${fallbackSvg}';">
        </div>
        <div style="flex: 1; min-width: 0;">
          <h4 style="font-size: 1.05rem; font-weight: 700; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${ball.brand} ${ball.name}</h4>
          <p style="font-size: 0.76rem; color: #94a3b8; margin: 2px 0 0;">${ball.coverType || 'Reactive'} • ${ball.finish || 'Factory Finish'} • ${ball.weight || 15} lbs</p>
        </div>
      </div>

      <div class="maint-modal-stats-grid">
        <div class="maint-stat-pill">
          <span class="maint-stat-label">TOTAL GAMES</span>
          <span class="maint-stat-val" style="color: var(--accent-cyan); font-weight: 800;">${maintStats.totalGames}</span>
        </div>
        <div class="maint-stat-pill">
          <span class="maint-stat-label">SINCE DETOX</span>
          <span class="maint-stat-val" style="color: var(--accent-amber); font-weight: 800;">${maintStats.gamesSinceDetox}</span>
        </div>
        <div class="maint-stat-pill">
          <span class="maint-stat-label">SINCE RESURFACE</span>
          <span class="maint-stat-val" style="color: #c084fc; font-weight: 800;">${maintStats.gamesSinceResurface}</span>
        </div>
      </div>

      <div class="maint-history-section">
        <div class="maint-history-title-row">
          <h5>Service History (${logs.length})</h5>
        </div>
        ${historyHtml}
      </div>
    `;

    // Twirl animation
    const avatarEl = maintLogBody.querySelector('.ball-avatar-circle');
    if (avatarEl) {
      avatarEl.addEventListener('click', () => {
        avatarEl.classList.remove('twirl-anim');
        void avatarEl.offsetWidth;
        avatarEl.classList.add('twirl-anim');
      });
    }

    // Delete single log entry handler
    maintLogBody.querySelectorAll('.btn-delete-log').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const logId = e.currentTarget.getAttribute('data-log-id');
        if (!confirm('Delete this maintenance record from history?')) return;

        const currentAppState = appState.get();
        const updatedArsenal = currentAppState.arsenal.map(b => {
          if (b.id === ballId) {
            const updatedLogs = (b.maintenanceLogs || []).filter(l => l.id !== logId);
            const remainingDetox = updatedLogs.find(l => {
              const act = (l.action || '').toLowerCase();
              return act.includes('detox') || act.includes('de-oil') || act.includes('deoil');
            });
            const remainingResurface = updatedLogs.find(l => {
              const act = (l.action || '').toLowerCase();
              return act.includes('resurface');
            });

            return {
              ...b,
              maintenanceLogs: updatedLogs,
              lastDetoxGames: remainingDetox ? (remainingDetox.gamesBowled ?? 0) : 0,
              lastDeoil: remainingDetox ? remainingDetox.date : 'None',
              lastResurfaceGames: remainingResurface ? (remainingResurface.gamesBowled ?? 0) : 0,
              lastResurfaceDate: remainingResurface ? remainingResurface.date : 'None'
            };
          }
          return b;
        });

        appState.set({ arsenal: updatedArsenal });
        openModalForMaintenanceLog(ballId);
        renderArsenal();
      });
    });

    const btnEmptyAdd = maintLogBody.querySelector('#btnEmptyLogAdd');
    if (btnEmptyAdd) {
      btnEmptyAdd.addEventListener('click', () => {
        openModalForMaint(ballId);
      });
    }

    if (window.lucide) {
      window.lucide.createIcons();
    }

    modalMaintLog.classList.remove('hidden');
  }

  function renderArsenal() {
    if (!gridContainer) return;

    const state = appState.get();
    let balls = state.arsenal;

    if (activeFilter !== 'all') {
      balls = balls.filter(b => b.coverType === activeFilter);
    }

    if (countEl) countEl.textContent = state.arsenal.length;

    gridContainer.innerHTML = '';

    if (balls.length === 0) {
      gridContainer.innerHTML = `<div class="empty-state card"><p>No balls found for filter "${activeFilter}".</p></div>`;
      return;
    }

    balls.forEach(ball => {
      const card = document.createElement('div');
      card.className = 'ball-card';

      let coverBadgeClass = 'badge-solid';
      if (ball.coverType && ball.coverType.includes('Pearl')) coverBadgeClass = 'badge-pearl';
      else if (ball.coverType && ball.coverType.includes('Hybrid')) coverBadgeClass = 'badge-hybrid';

      const color1 = (ball.colors && ball.colors[0]) ? ball.colors[0] : '#ff5722';
      const color2 = (ball.colors && ball.colors[1]) ? ball.colors[1] : '#b71c1c';
      const color3 = (ball.colors && ball.colors[2]) ? ball.colors[2] : '#4a148c';

      const photoUrl = getBallPhotoUrl(ball);
      const fallbackSvg = generate3DBallSVG(ball.colors, ball.coverType);
      const maintStats = getBallMaintenanceStats(ball);

      const ballSphereArt = `
        <div class="ball-avatar-circle" title="Tap or hover to twirl!" style="background: radial-gradient(circle at 18px 18px, ${color1} 0%, ${color2} 60%, ${color3} 100%);">
          <img src="${photoUrl}" alt="${ball.name}" class="ball-img-photo" onerror="this.onerror=null; this.src='${fallbackSvg}';">
          <div class="mini-grip-dots"></div>
        </div>
      `;

      card.innerHTML = `
        <div class="ball-card-header">
          ${ballSphereArt}
          <div style="flex: 1; min-width: 0;">
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <h3 style="font-size: 1rem; font-weight: 700;">${ball.brand} ${ball.name}</h3>
              <div style="display: flex; align-items: center; gap: 6px;">
                <span class="ball-cover-badge ${coverBadgeClass}">${ball.coverType || 'Reactive'}</span>
                <button class="ball-delete-btn" data-id="${ball.id}" title="Remove Ball from Bag">
                  <i data-lucide="trash-2"></i>
                </button>
              </div>
            </div>
            <p class="ball-meta">${ball.coverName || ball.finish || 'Factory Finish'} • ${ball.weight || 15} lbs</p>
          </div>
        </div>

        <div class="ball-specs-row">
          <div class="spec-item spec-item-3">
            <span class="label">RG / DIFF</span>
            <span class="val">${ball.rg ? ball.rg.toFixed(2) : '2.48'} / ${ball.diff ? ball.diff.toFixed(3) : '0.051'}</span>
          </div>
          <div class="spec-item spec-item-3">
            <span class="label">SURFACE</span>
            <span class="val">${ball.finish || '2000 Grit'}</span>
          </div>
          <div class="spec-item spec-item-3">
            <span class="label">GAMES BOWLED</span>
            <span class="val" style="color: var(--accent-cyan); font-weight: 800;">${maintStats.totalGames}</span>
          </div>
          <div class="spec-item spec-item-2">
            <span class="label">GAMES SINCE DETOX</span>
            <span class="val" style="color: var(--accent-amber); font-weight: 800;">${maintStats.gamesSinceDetox}</span>
          </div>
          <div class="spec-item spec-item-2">
            <span class="label">GAMES SINCE RESURFACE</span>
            <span class="val" style="color: #c084fc; font-weight: 800;">${maintStats.gamesSinceResurface}</span>
          </div>
        </div>

        <div class="ball-card-actions">
          <button class="btn-action-edit" data-id="${ball.id}">
            <i data-lucide="edit-3"></i> Edit
          </button>
          <button class="btn-action-maint-log" data-id="${ball.id}">
            <i data-lucide="clipboard-list"></i> Maintenance Log
          </button>
          <button class="btn-action-maint" data-id="${ball.id}">
            <i data-lucide="wrench"></i> Surface
          </button>
        </div>
      `;

      // Interactive Twirl Animation Click Handler
      const avatarEl = card.querySelector('.ball-avatar-circle');
      if (avatarEl) {
        avatarEl.addEventListener('click', () => {
          avatarEl.classList.remove('twirl-anim');
          void avatarEl.offsetWidth; // trigger reflow
          avatarEl.classList.add('twirl-anim');
        });
      }

      gridContainer.appendChild(card);
    });

    if (window.lucide) {
      window.lucide.createIcons();
    }

    // Bind Edit Ball buttons
    gridContainer.querySelectorAll('.btn-action-edit').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.getAttribute('data-id');
        openModalForEdit(id);
      });
    });

    // Bind Maintenance Log buttons
    gridContainer.querySelectorAll('.btn-action-maint-log').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.getAttribute('data-id');
        openModalForMaintenanceLog(id);
      });
    });

    // Bind Surface Maintenance Log buttons
    gridContainer.querySelectorAll('.btn-action-maint').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.getAttribute('data-id');
        openModalForMaint(id);
      });
    });

    // Bind Delete Ball buttons
    gridContainer.querySelectorAll('.ball-delete-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = e.currentTarget.getAttribute('data-id');
        const b = state.arsenal.find(x => x.id === id);
        if (!b) return;

        if (confirm(`Remove "${b.brand} ${b.name}" from your Arsenal?`)) {
          const s = appState.get();
          const filteredArsenal = s.arsenal.filter(x => x.id !== id);
          appState.set({ arsenal: filteredArsenal });
          renderArsenal();
        }
      });
    });
  }

  // Filter click handlers
  filterChips.forEach(chip => {
    chip.addEventListener('click', () => {
      filterChips.forEach(c => c.classList.remove('active'));
      chip.classList.add('active');
      activeFilter = chip.getAttribute('data-filter');
      renderArsenal();
    });
  });

  // Add Ball Modal handlers
  if (btnAddBall) {
    btnAddBall.addEventListener('click', openModalForAdd);
  }

  const closeAddBallModal = () => {
    editingBallId = null;
    updateSearchStatus('hidden');
    if (modalAddBall) modalAddBall.classList.add('hidden');
  };

  if (btnCloseAddBall) btnCloseAddBall.addEventListener('click', closeAddBallModal);
  if (btnCancelAddBall) btnCancelAddBall.addEventListener('click', closeAddBallModal);

  if (formAddBall) {
    formAddBall.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = document.getElementById('inputBallName').value.trim();
      const brand = document.getElementById('inputBallBrand').value.trim();
      const coverType = document.getElementById('inputCoverType').value;
      const finish = document.getElementById('inputFinish').value.trim() || 'Factory Finish';
      const rg = parseFloat(document.getElementById('inputRG').value) || 2.48;
      const diff = parseFloat(document.getElementById('inputDiff').value) || 0.050;
      const weightEl = document.getElementById('inputWeight');
      const weight = weightEl ? (parseInt(weightEl.value) || 15) : 15;

      if (!name || !brand) return;

      const s = appState.get();

      if (editingBallId) {
        // Edit existing ball
        const updatedArsenal = s.arsenal.map(b => {
          if (b.id === editingBallId) {
            return {
              ...b,
              name,
              brand,
              coverType,
              finish,
              rg,
              diff,
              weight,
              colors: selectedCatalogColors || b.colors || ['#00f2fe', '#4facfe', '#111111']
            };
          }
          return b;
        });
        appState.set({ arsenal: updatedArsenal });
      } else {
        // Add new ball
        const catalogMatch = PRO_SHOP_BALL_CATALOG.find(b => b.name.toLowerCase() === name.toLowerCase());
        const newBall = {
          id: 'ball_' + Date.now(),
          name,
          brand,
          coverType,
          coverName: catalogMatch?.coverName || finish,
          coreName: catalogMatch?.coreName || 'Core',
          finish,
          rg,
          diff,
          weight,
          colors: selectedCatalogColors || catalogMatch?.colors || ['#00f2fe', '#4facfe', '#111111'],
          gamesBowled: 0,
          lastDeoil: 'New',
          lastSanding: finish,
          lastDetoxGames: 0,
          lastResurfaceGames: 0,
          maintenanceLogs: []
        };
        appState.set({ arsenal: [newBall, ...s.arsenal] });
      }

      formAddBall.reset();
      closeAddBallModal();
      renderArsenal();
    });
  }

  // Maintenance Modal Handlers
  const closeMaintModal = () => {
    targetMaintBallId = null;
    if (modalMaint) modalMaint.classList.add('hidden');
  };
  if (btnCloseMaint) btnCloseMaint.addEventListener('click', closeMaintModal);
  if (btnCancelMaint) btnCancelMaint.addEventListener('click', closeMaintModal);

  // Maintenance Log Modal Handlers
  const closeMaintLogModal = () => {
    activeLogBallId = null;
    if (modalMaintLog) modalMaintLog.classList.add('hidden');
  };
  if (btnCloseMaintLog) btnCloseMaintLog.addEventListener('click', closeMaintLogModal);
  if (btnCloseMaintLogFooter) btnCloseMaintLogFooter.addEventListener('click', closeMaintLogModal);
  if (btnOpenAddMaintFromLog) {
    btnOpenAddMaintFromLog.addEventListener('click', () => {
      if (activeLogBallId) {
        openModalForMaint(activeLogBallId);
      }
    });
  }

  if (btnSaveMaint) {
    btnSaveMaint.addEventListener('click', () => {
      if (!targetMaintBallId) return;
      const s = appState.get();
      const ball = s.arsenal.find(b => b.id === targetMaintBallId);
      if (!ball) return;

      const action = maintActionSelect ? maintActionSelect.value : 'Surface Touch-up';
      const newGrit = maintGritInput ? maintGritInput.value.trim() : '';
      const notesVal = maintNotesInput ? maintNotesInput.value.trim() : '';
      const dateVal = (maintDateInput && maintDateInput.value) ? maintDateInput.value : new Date().toISOString().slice(0, 10);
      const parsedGames = maintGamesInput ? parseInt(maintGamesInput.value, 10) : NaN;
      const gamesVal = Number.isFinite(parsedGames) ? Math.max(0, parsedGames) : (ball.gamesBowled || 0);

      const logEntry = {
        id: 'maint_' + Date.now(),
        date: dateVal,
        action,
        grit: newGrit || ball.finish || '',
        notes: notesVal,
        gamesBowled: gamesVal,
        createdAt: new Date().toISOString()
      };

      const isDetox = action.toLowerCase().includes('detox') || action.toLowerCase().includes('de-oil') || action.toLowerCase().includes('deoil');
      const isResurface = action.toLowerCase().includes('resurface');
      const isTouchUp = action.toLowerCase().includes('touch-up') || action.toLowerCase().includes('sanding');

      const updatedArsenal = s.arsenal.map(b => {
        if (b.id === targetMaintBallId) {
          const updatedLogs = [logEntry, ...(b.maintenanceLogs || [])];
          return {
            ...b,
            finish: newGrit || b.finish,
            lastSanding: (isResurface || isTouchUp) ? `${action} (${newGrit || b.finish})` : b.lastSanding,
            maintenanceLogs: updatedLogs,
            ...(isDetox ? { lastDetoxGames: gamesVal, lastDeoil: dateVal } : {}),
            ...(isResurface ? { lastResurfaceGames: gamesVal, lastResurfaceDate: dateVal } : {})
          };
        }
        return b;
      });

      const currentBallId = targetMaintBallId;
      appState.set({ arsenal: updatedArsenal });
      closeMaintModal();

      if (modalMaintLog && !modalMaintLog.classList.contains('hidden') && activeLogBallId === currentBallId) {
        openModalForMaintenanceLog(currentBallId);
      }

      renderArsenal();
    });
  }

  initCatalogDropdownAndSearch();
  renderArsenal();
  appState.subscribe(renderArsenal);
}
